import { mouseAndWheelHorizontalScrollEvents, mouseSwipe } from '../js/helpers/mouseAndWheelHorizontalScrollEvents';
import { debounce } from '../js/debounce';
const observer = new ResizeObserver(entries => {
    for (const entry of entries) {
        if (entry.contentRect.width > 0) {
            entry.target.init();
            observer.unobserve(entry.target);
        }
    }
});
const ACTIVE_PAGINATOR_ITEM_ATTR_NAME = 'data-slider-paginator-item-active';
class ComponentSlider extends HTMLElement {
    slider;
    sliderMode = 'slideshow';
    btnNext = null;
    btnPrev = null;
    isPaginate = false;
    connectedCallback() {
        const slider = this.querySelector('[data-slider]');
        this.sliderMode = this.dataset.sliderMode ?? this.sliderMode;
        this.isPaginate = this.hasAttribute('data-slider-paginate');
        if (slider === null ||
            (this.dataset.sliderInitFrom !== undefined &&
                !window.matchMedia(`(min-width: ${this.dataset.sliderInitFrom}px)`).matches))
            return;
        this.slider = slider;
        this.btnPrev = this.querySelector('[data-slider-prev]');
        this.btnNext = this.querySelector('[data-slider-next]');
        this.init = this.init.bind(this);
        if (this.clientWidth === 0) {
            observer.observe(this);
            return;
        }
        this.init();
    }
    init() {
        if (this.slider.scrollWidth <= this.slider.clientWidth)
            return;
        this.initButtons();
        if (this.sliderMode === 'slider') {
            window.innerWidth > 1200 && this.slider.addEventListener('mousedown', event => mouseSwipe(event, this.swipeHandle));
            this.isPaginate && this.initPaginator();
            return;
        }
        window.innerWidth > 1200 && this.slider.addEventListener('mousedown', mouseAndWheelHorizontalScrollEvents.mousedown);
    }
    swipeHandle = (direction) => {
        const handles = {
            swipedLeft: () => this.scrollByDistance(this.getScrollDistance()),
            swipedRight: () => this.scrollByDistance(-this.getScrollDistance())
        };
        handles[direction]();
    };
    initPaginator = () => {
        const markVisibleSlides = debounce(this.markVisibleItems);
        const items = this.getListOfItems();
        const paginator = this.createPagination(items.length);
        this.slider.addEventListener('scroll', () => markVisibleSlides(items, paginator));
        markVisibleSlides(items, paginator);
    };
    markVisibleItems = (items, paginator) => {
        for (let i = 0; i < items.length; i++) {
            const sliderItem = items[i];
            const paginationItem = paginator.children[i];
            const { left: sliderLeft, right: sliderRight } = this.slider.getBoundingClientRect();
            const { left: itemLeft, right: itemRight } = sliderItem.getBoundingClientRect();
            if (itemRight - 5 < sliderLeft || itemLeft + 5 > sliderRight) {
                if (!paginationItem.hasAttribute(ACTIVE_PAGINATOR_ITEM_ATTR_NAME))
                    continue;
                paginationItem.removeAttribute(ACTIVE_PAGINATOR_ITEM_ATTR_NAME);
                continue;
            }
            if (itemRight - 5 > sliderLeft && itemLeft + 5 < sliderRight) {
                if (paginationItem.hasAttribute(ACTIVE_PAGINATOR_ITEM_ATTR_NAME))
                    continue;
                paginationItem.setAttribute(ACTIVE_PAGINATOR_ITEM_ATTR_NAME, '');
            }
        }
    };
    createPagination = (itemsLength) => {
        const paginationEl = document.createElement('ul');
        paginationEl.setAttribute('data-slider-paginator', '');
        for (let i = 0; i < itemsLength; i++) {
            const item = document.createElement('li');
            item.addEventListener('click', () => {
                this.slider.scrollTo({
                    top: 0,
                    left: this.slider.children[i].offsetLeft,
                    behavior: 'smooth'
                });
            });
            paginationEl.appendChild(item);
        }
        this.appendChild(paginationEl);
        return paginationEl;
    };
    getListOfItems() {
        let items = this.slider.children;
        if (items.length === 1 && items[0].scrollWidth > this.slider.clientWidth) {
            items = items[0].children;
        }
        return items;
    }
    scrollByDistance(distance) {
        let nextPos = this.slider.scrollLeft + distance;
        if (nextPos > 0 && nextPos < this.slider.scrollWidth) {
            for (const item of this.getListOfItems()) {
                const { offsetLeft, offsetWidth } = item;
                if ((offsetLeft <= nextPos && offsetLeft + offsetWidth > nextPos) ||
                    (offsetLeft >= nextPos)) {
                    nextPos = offsetLeft;
                    break;
                }
            }
        }
        this.slider.scrollTo({
            top: 0,
            left: nextPos,
            behavior: 'smooth'
        });
    }
    getScrollDistance() {
        const items = this.getListOfItems();
        let distance = this.slider.clientWidth;
        if (items.length > 1) {
            const computedStyles = getComputedStyle(items[1]);
            const sliderComputedStyle = getComputedStyle(this.slider);
            const sliderColumnGap = sliderComputedStyle.display === 'grid' ? parseFloat(sliderComputedStyle.columnGap) : 0;
            const sliderPaddings = parseFloat(sliderComputedStyle.paddingLeft) + parseFloat(sliderComputedStyle.paddingRight);
            const margins = parseInt(computedStyles.marginRight) + parseInt(computedStyles.marginLeft);
            if (!isNaN(sliderColumnGap)) {
                distance += sliderColumnGap;
            }
            if (!isNaN(sliderPaddings)) {
                distance -= sliderPaddings;
            }
            if (!isNaN(margins)) {
                distance += margins;
            }
        }
        return distance;
    }
    updateButtons() {
        if (this.slider.scrollWidth <= this.slider.clientWidth) {
            if (this.btnNext !== null) {
                this.btnNext.setAttribute('aria-hidden', 'true');
            }
            if (this.btnPrev !== null) {
                this.btnPrev.setAttribute('aria-hidden', 'true');
            }
        }
    }
    initButtons() {
        this.btnPrev?.addEventListener('click', () => {
            this.scrollByDistance(-this.getScrollDistance());
        });
        if (this.btnNext !== null) {
            this.btnNext.addEventListener('click', () => {
                this.scrollByDistance(this.getScrollDistance());
            });
            this.btnNext.setAttribute('aria-hidden', 'false');
        }
        this.slider.addEventListener('scroll', () => {
            this.btnPrev?.setAttribute('aria-hidden', this.slider.scrollLeft > 2 ? 'false' : 'true');
            this.btnNext?.setAttribute('aria-hidden', this.slider.scrollWidth - this.slider.clientWidth - this.slider.scrollLeft < 2 ? 'true' : 'false');
        });
    }
    update() {
        const slider = this.querySelector('[data-slider]');
        if (slider === null || (this.dataset.sliderInitFrom !== undefined && window.innerWidth < +this.dataset.sliderInitFrom))
            return;
        this.slider = slider;
        this.updateButtons();
    }
}
customElements.define('slider-component', ComponentSlider);
